import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ArrowUp, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";
import { SectionHeading } from "@/components/site/SectionHeading";
import { RulesTopics } from "@/components/site/rules/RulesTopics";
import { RulesSearchResults } from "@/components/site/rules/RulesSearchResults";
import { RulesPanel } from "@/components/site/rules/RulesPanel";
import { VideoBackdrop } from "@/components/site/VideoBackdrop";

function normalize(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function useRulesData(preview) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(preview ? "/admin/rules" : "/rules")
      .then(({ data: d }) => alive && setData(d))
      .catch(() => alive && setData({ categories: [], rules: [], error: true }));
    return () => { alive = false; };
  }, [preview]);

  const categories = useMemo(() => {
    if (!data) return [];
    return data.categories.map((c, ci) => ({
      ...c,
      number: ci + 1,
      rules: data.rules.filter((r) => r.category_id === c.id).map((r, ri) => ({ ...r, number: `${ci + 1}.${ri + 1}`, anchor: `regra-${ci + 1}-${ri + 1}` })),
    }));
  }, [data]);

  return { data, categories };
}

function useBackToTop() {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return showTop;
}

export default function Regras() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const { preview } = useContent();
  const t = useText();
  const { data, categories } = useRulesData(preview);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(null);
  const showTop = useBackToTop();
  const suffix = preview ? "?preview=1" : "";

  const activeSlug = slug || categories[0]?.slug;
  const activeIndex = categories.findIndex((c) => c.slug === activeSlug);
  const activeCat = categories[activeIndex];

  useEffect(() => {
    if (!hash || !categories.length) return undefined;
    const anchor = hash.slice(1);
    const t = setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ block: "center" }), 80);
    return () => clearTimeout(t);
  }, [hash, categories, activeSlug]);

  useEffect(() => {
    if (!hash && slug) window.scrollTo({ top: Math.min(window.scrollY, 260) });
  }, [slug, hash]);

  const copyLink = useCallback(async (r, c) => {
    const url = `${window.location.origin}/regras/${c.slug}#${r.anchor}`;
    const text = `**Regra ${r.number}${r.title ? `: ${r.title}` : ""}** (${c.title})\n${url}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(r.anchor);
      toast.success(`Ligação da regra ${r.number} copiada. Cola no Discord.`);
      setTimeout(() => setCopied((cur) => (cur === r.anchor ? null : cur)), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copia o endereço da barra do navegador.");
    }
  }, []);

  const q = normalize(query.trim());
  const searching = q.length > 0;
  const results = useMemo(() => {
    if (!q) return [];
    const match = (r, c) => r.number.includes(q) || [r.title, r.body, r.group, c.title].some((v) => normalize(v).includes(q));
    return categories.flatMap((c) => c.rules.filter((r) => match(r, c)).map((r) => ({ ...r, category: c })));
  }, [categories, q]);

  const totalRules = categories.reduce((n, c) => n + c.rules.length, 0);

  return (
    <section className="relative py-16 sm:py-24" aria-labelledby="regras-title" data-testid="rules-page">
      <VideoBackdrop opacity={0.22} className="h-[520px]" />
      <div className="container-eden relative">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading id="regras-title" eyebrow={t("rules_eyebrow")} title={t("rules_title")} text={data?.intro || `${totalRules} regras em ${categories.length} categorias.`} />
          <label className="relative block w-full lg:max-w-sm">
            <span className="sr-only">Pesquisar regras</span>
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-eden-muted" aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Pesquisar em ${totalRules} regras`} className="input-eden h-12 pl-10" data-testid="rules-search-input" />
          </label>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <RulesTopics categories={categories} activeSlug={activeSlug} suffix={suffix} onSelect={() => setQuery("")} />

          <div className="lg:col-span-8">
            {!data && <p className="text-sm text-eden-muted" role="status">A carregar regras…</p>}
            {data?.error && <p className="text-eden-secondary" role="alert">Não foi possível carregar as regras.</p>}
            {searching && <RulesSearchResults results={results} query={query} suffix={suffix} onPick={() => setQuery("")} />}
            {!searching && (
              <div data-testid="rules-categories">
                <RulesPanel category={activeCat} prev={categories[activeIndex - 1]} next={categories[activeIndex + 1]} suffix={suffix} copied={copied} onCopy={copyLink} onSelect={() => setQuery("")} />
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0 })}
        className={`fixed bottom-[4.75rem] right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-eden-border bg-eden-surface text-eden-text transition-[opacity,transform] duration-200 hover:border-eden-orange ${showTop ? "opacity-100" : "pointer-events-none translate-y-2 opacity-0"}`}
        aria-label="Voltar ao topo"
        data-testid="back-to-top-button"
      >
        <ArrowUp size={18} />
      </button>
    </section>
  );
}
